import { parseUserAgent } from './ua';
import type { DeviceType } from './ua';
import type { ConsoleErrorEntry } from './console-buffer';

export interface IdentifyPayload {
  userId?: string;
  name?: string;
  email?: string;
  role?: string;
  [key: string]: unknown;
}

export interface CreateMetadataStoreOptions {
  /** Getter for recently-captured console errors/warnings, if console capture is attached. */
  getConsoleErrors?: () => ConsoleErrorEntry[];
}

export interface CollectedMetadata {
  url: string;
  title: string;
  referrer: string;
  viewport: { w: number; h: number };
  screen: { w: number; h: number };
  language: string;
  timezone: string;
  timestamp: string;
  browser: string;
  browserVersion?: string;
  os: string;
  deviceType: DeviceType;
  user?: Record<string, unknown>;
  custom: Record<string, unknown>;
  release?: string;
  consoleErrors?: ConsoleErrorEntry[];
}

export interface MetadataStore {
  identify(user: IdentifyPayload): void;
  setContext(ctx: Record<string, unknown>): void;
  setRelease(release: string): void;
  collect(): CollectedMetadata;
}

// Every browser-global read below is guarded: this module runs both in real
// browsers (many host-page environments, some hardened/locked-down) and
// under jsdom in tests, where APIs like `navigator.connection` or a full
// `Intl` implementation may be partial or absent. `collect()` must never
// throw regardless of environment.

function safeUrl(): string {
  try {
    return window?.location?.href ?? '';
  } catch {
    return '';
  }
}

function safeTitle(): string {
  try {
    return document?.title ?? '';
  } catch {
    return '';
  }
}

function safeReferrer(): string {
  try {
    return document?.referrer ?? '';
  } catch {
    return '';
  }
}

function safeViewport(): { w: number; h: number } {
  try {
    return { w: window?.innerWidth ?? 0, h: window?.innerHeight ?? 0 };
  } catch {
    return { w: 0, h: 0 };
  }
}

function safeScreen(): { w: number; h: number } {
  try {
    return { w: window?.screen?.width ?? 0, h: window?.screen?.height ?? 0 };
  } catch {
    return { w: 0, h: 0 };
  }
}

function safeLanguage(): string {
  try {
    return navigator?.language ?? 'en';
  } catch {
    return 'en';
  }
}

function safeTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

function safeUaFields(): {
  browser: string;
  browserVersion?: string;
  os: string;
  deviceType: DeviceType;
} {
  try {
    const ua = navigator?.userAgent ?? '';
    return parseUserAgent(ua);
  } catch {
    return { browser: 'Unknown', os: 'Unknown', deviceType: 'desktop' };
  }
}

export function createMetadataStore(options: CreateMetadataStoreOptions = {}): MetadataStore {
  let user: Record<string, unknown> | undefined;
  let custom: Record<string, unknown> = {};
  let release: string | undefined;

  return {
    identify(payload) {
      user = { ...payload };
    },

    setContext(ctx) {
      custom = { ...custom, ...ctx };
    },

    setRelease(value) {
      release = value;
    },

    collect() {
      const uaFields = safeUaFields();

      const metadata: CollectedMetadata = {
        url: safeUrl(),
        title: safeTitle(),
        referrer: safeReferrer(),
        viewport: safeViewport(),
        screen: safeScreen(),
        language: safeLanguage(),
        timezone: safeTimezone(),
        timestamp: new Date().toISOString(),
        browser: uaFields.browser,
        browserVersion: uaFields.browserVersion,
        os: uaFields.os,
        deviceType: uaFields.deviceType,
        custom,
      };

      if (user) {
        metadata.user = user;
      }

      if (release) {
        metadata.release = release;
      }

      if (options.getConsoleErrors) {
        // Guard like every other field so a throwing getter can never make
        // collect() throw (the widget must never throw into the host app).
        try {
          metadata.consoleErrors = options.getConsoleErrors();
        } catch {
          // omit consoleErrors if the getter fails
        }
      }

      return metadata;
    },
  };
}
